import { axios } from "..";
import { IEvent } from "../interfaces/models/IEvent";
import {
  isAzureBlobUrl,
  normalizeDocumentUrl,
} from "../utils/normalizeDocumentUrl";

export type EventLookupOption = Pick<IEvent, "id" | "title"> & {
  legacyId?: string | number;
};

const EVENT_LOOKUP_FIELDS: Array<keyof EventLookupOption> = [
  "id",
  "title",
  "legacyId",
];

export function formatEventLookupLabel(event: EventLookupOption): string {
  const title = String(event.title ?? "").trim();
  const legacyId =
    event.legacyId == null ? "" : String(event.legacyId).trim();
  if (legacyId && title) {
    return `${legacyId} - ${title}`;
  }
  if (title) {
    return title;
  }
  if (legacyId) {
    return legacyId;
  }
  return String(event.id ?? "").trim();
}

export const getEventLookupOptions = async (): Promise<EventLookupOption[]> => {
  try {
    const params = new URLSearchParams();
    params.append("fields", JSON.stringify(EVENT_LOOKUP_FIELDS));
    params.append("sort", JSON.stringify({ field: "title", order: "asc" }));
    const response = await axios.Get(`./event?${params.toString()}`);
    return Array.isArray(response.data) ? response.data : [];
  } catch {
    return [];
  }
};

export const getEvents = async (): Promise<IEvent[]> => {
  try {
    const response = await axios.Get("./event");
    return response.data;
  } catch (error) {
    return [];
  }
};

export const getEventById = async (id: string, withWSG: boolean = true): Promise<IEvent | null> => {
  try {
    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({ id: { value: id, comparisonType: "EQUAL" } })
    );
    if (withWSG) {
      params.append("withWSG", "true");
    }
    const response = await axios.Get(`./event?${params.toString()}`);
    return Array.isArray(response.data) ? response.data[0] : response.data;
  } catch (error) {
    return null;
  }
};

export const fetchSecurePresentation = async (
  eventId: string,
  programId?: string,
): Promise<Blob> => {
  const params = new URLSearchParams();
  if (programId) {
    params.append("programId", programId);
  }
  const query = params.toString();
  const path = `./event-presentation/${eventId}${query ? `?${query}` : ""}`;
  const response = await axios.GetArrayBuffer(path);
  return new Blob([response.data], { type: "application/pdf" });
};

export const fetchPresentationPdf = async (
  eventId: string,
  documentUrl: string,
  programId?: string,
): Promise<Blob> => {
  const normalizedUrl = normalizeDocumentUrl(documentUrl);

  if (isAzureBlobUrl(normalizedUrl)) {
    return fetchSecurePresentation(eventId, programId);
  }

  const response = await fetch(normalizedUrl, {
    credentials: "include",
    mode: "cors",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch presentation (${response.status})`);
  }

  const blob = await response.blob();
  return blob.type.includes("pdf")
    ? blob
    : new Blob([await blob.arrayBuffer()], { type: "application/pdf" });
};

export const createEvent = async (event: IEvent): Promise<IEvent> => {
  try {
    const response = await axios.Post("./event", event);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateEvent = async (event: IEvent): Promise<IEvent> => {
  try {
    const response = await axios.Put(`./event`, event);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteEvent = async (id: string): Promise<any> => {
  try {
    const response = await axios.Delete(`./event`, `${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getEventsByFilter = async (
  filter: any,
  sort?: any,
  pagination?: any
): Promise<{ data: IEvent[]; totalCount: number }> => {
  try {
    const params = new URLSearchParams();
    if (filter) {
      params.append("filter", JSON.stringify(filter));
    }
    if (sort) {
      params.append("sort", JSON.stringify(sort));
    }
    if (pagination) {
      params.append("pagination", JSON.stringify(pagination));
    }

    const baseUrl = `./event?${params.toString()}`;
    const countUrl = `${baseUrl}${params.toString() ? "&" : ""}count=true`;

    const [dataResponse, countResponse] = await Promise.all([
      axios.Get(baseUrl),
      axios.Get(countUrl),
    ]);

    const totalCount = Array.isArray(countResponse.data)
      ? countResponse.data[0] || 0
      : countResponse.data || 0;

    return {
      data: Array.isArray(dataResponse.data) ? dataResponse.data : [],
      totalCount: Number(totalCount) || 0,
    };
  } catch (error) {
    return { data: [], totalCount: 0 };
  }
};
